import * as React from 'react';
import { setDebugMode, isDebugMode } from '@/services/loggingService';

interface DebugModeContextValue {
  debugMode: boolean;
  toggleDebugMode: () => void;
  enableDebugMode: () => void;
  disableDebugMode: () => void;
}

const DebugModeContext = React.createContext<DebugModeContextValue | undefined>(undefined);

const DEBUG_MODE_STORAGE_KEY = 'aps-debug-mode';

/**
 * Get initial debug mode state from localStorage or environment
 */
const getInitialDebugMode = (): boolean => {
  if (typeof window === 'undefined') return false;

  // Check localStorage first
  const stored = localStorage.getItem(DEBUG_MODE_STORAGE_KEY);
  if (stored !== null) {
    return stored === 'true';
  }

  // Default to environment setting
  return isDebugMode();
};

export const DebugModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [debugMode, setDebugModeState] = React.useState<boolean>(() => getInitialDebugMode());

  // Sync debug mode state with logging service
  const updateDebugMode = React.useCallback((enabled: boolean) => {
    setDebugModeState(enabled);
    setDebugMode(enabled);

    // Persist to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem(DEBUG_MODE_STORAGE_KEY, String(enabled));
    }
  }, []);

  // Initialize logging service on mount
  React.useEffect(() => {
    setDebugMode(debugMode);
  }, [debugMode]);

  const toggleDebugMode = React.useCallback(() => {
    updateDebugMode(!debugMode);
  }, [debugMode, updateDebugMode]);

  const enableDebugMode = React.useCallback(() => {
    updateDebugMode(true);
  }, [updateDebugMode]);

  const disableDebugMode = React.useCallback(() => {
    updateDebugMode(false);
  }, [updateDebugMode]);

  const value = React.useMemo<DebugModeContextValue>(
    () => ({
      debugMode,
      toggleDebugMode,
      enableDebugMode,
      disableDebugMode,
    }),
    [debugMode, toggleDebugMode, enableDebugMode, disableDebugMode]
  );

  return <DebugModeContext.Provider value={value}>{children}</DebugModeContext.Provider>;
};

export const useDebugMode = (): DebugModeContextValue => {
  const context = React.useContext(DebugModeContext);
  if (!context) {
    throw new Error('useDebugMode must be used within a DebugModeProvider');
  }
  return context;
};
